import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';

import { AdType } from './enums';

import { LeaderAd, MRecAd, TakeoverAd } from './styles';

type Props = {
    divId: string;
    adType: AdType;
    forceReload?: boolean;
};

const AdTag: FC<Props> = ({ divId, adType, forceReload = false }) => {
    const size = useSizeWithThrottle();

    const [currentScreenWidth, setCurrentScreenWidth] = useState<number>(size.width);
    const [currentBreakpoint, setCurrentBreakpoint] = useState<number>(size.width);

    const breakpoints = useMemo(() => {
        switch (adType) {
            case AdType.HEADER:
                return window.ADV_RESPONSIVE_MAP['leaderboard-large'].map((adSizeMap) => adSizeMap.breakpoint[0]);
            case AdType.LEADER:
                return window.ADV_RESPONSIVE_MAP.leaderboard.map((adSizeMap) => adSizeMap.breakpoint[0]);
            case AdType.MEDIUM_RECTANGLE:
                return window.ADV_RESPONSIVE_MAP.mrec.map((adSizeMap) => adSizeMap.breakpoint[0]);
            case AdType.PRESBY:
                return window.ADV_RESPONSIVE_MAP.sponsorship.map((adSizeMap) => adSizeMap.breakpoint[0]);
            default:
                // if we know nothing about this ad, then we give it a breakpoint of 0 so it never refreshes
                return [0];
        }
    }, [adType]);

    const slot = useMemo(() => {
        window.googletag = window.googletag || { cmd: [] };
        if (window.googletag.pubads) {
            return window.googletag
                .pubads()
                .getSlots()
                .find((slot) => slot.getSlotElementId() === divId);
        }

        return null;
    }, [divId]);

    const refreshAd = useCallback(() => {
        window.googletag = window.googletag || { cmd: [] };

        if (slot && window.googletag.pubads) {
            window.googletag.pubads().refresh([slot]);
        }
    }, [slot]);

    useEffect(() => {
        const newScreenWidth = size.width;

        if (currentScreenWidth !== newScreenWidth) {
            setCurrentScreenWidth(newScreenWidth);

            const largestBreakpointForScreenWidth = breakpoints.find((breakpoint) => newScreenWidth >= breakpoint);

            if (largestBreakpointForScreenWidth && largestBreakpointForScreenWidth !== currentBreakpoint) {
                setCurrentBreakpoint(largestBreakpointForScreenWidth);
                refreshAd();
            }
        }
    }, [breakpoints, currentScreenWidth, currentBreakpoint, refreshAd, size, slot]);

    const displayAdTag = useCallback(
        (node: HTMLDivElement) => {
            if (node !== null) {
                window.googletag = window.googletag || { cmd: [] };

                window.googletag.cmd.push(() => {
                    window.googletag.display(divId);
                    forceReload && refreshAd();
                });
            }
        },
        [divId, forceReload, refreshAd]
    );

    if (divId) {
        switch (adType) {
            case (AdType.LEADER, AdType.HEADER):
                return <LeaderAd ref={displayAdTag} id={divId} />;
            case AdType.TAKEOVER:
                return <TakeoverAd ref={displayAdTag} id={divId} />;
            case AdType.PRESBY:
                return <div ref={displayAdTag} id={divId} />;
            case AdType.MEDIUM_RECTANGLE:
                return <MRecAd ref={displayAdTag} id={divId} />;
        }
    }

    return <></>;
};

export default AdTag;
